'use client';

import React from 'react';
import { Chip, Tooltip } from '@heroui/react';
import { Icon } from '@iconify/react';
import { ResponsiveTooltip, Typography } from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';
import { RevshareCampaign } from '../types';
import { IcDate, InfoCircle, RefreshGreyIc } from '../../../../components/icons';
import { formatTimeAgo } from '../../../../utils/formatTimeAgo';

interface StatusCellProps {
  campaign: RevshareCampaign;
}

const StatusCell: React.FC<StatusCellProps> = ({ campaign }) => {
  const { t } = useTranslations();

  // Map API status to display status with colors matching design system
  const getStatusChip = (status: string) => {
    switch (status) {
      case 'in_progress':
        return {
          text: t('partner.partnerCampaigns.filters.statusActive') || 'Активный',
          color: '#17C964',
          dotColor: 'bg-success',
        };
      case 'waiting':
        return {
          text: t('partner.partnerCampaigns.filters.statusWaiting') || 'Ожидание',
          color: '#F5A524',
          dotColor: 'bg-warning',
        };
      case 'in_moderation':
      case 'moderation':
        return {
          text: t('partner.partnerCampaigns.filters.statusModeration') || 'На модерации',
          color: '#7828C8',
          dotColor: 'bg-accent',
        };
      case 'canceled':
        return {
          text: t('partner.partnerCampaigns.filters.statusCanceled') || 'Отменена',
          color: '#F31260',
          dotColor: 'bg-auth-strength-weak',
        };
      case 'completed':
      default:
        return {
          text: t('partner.partnerCampaigns.filters.statusInactive') || 'Неактивный',
          color: '#71717A',
          dotColor: 'bg-auth-muted',
        };
    }
  };

  const displayStatus = getStatusChip(campaign.status);
  const isInModeration = campaign.status === 'in_moderation' || campaign.status === 'moderation';

  // Format campaign start date
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Format last update time
  const lastUpdate = campaign.last_update || campaign.created_at;
  const timeAgo = lastUpdate ? formatTimeAgo(lastUpdate, t) : '';

  return (
    <div className="flex flex-col gap-1 py-2">
      {/* Status Chip */}
      <div className="flex items-center gap-1">
        <Chip
          className="rounded-lg bg-auth-surface text-auth-ink"
          classNames={{
            base: 'h-6 px-2 py-1 gap-1',
          }}
          size="sm"
          startContent={
            <div className={`size-[0.4375rem] rounded-full ${displayStatus.dotColor}`}></div>
          }
        >
          <Typography variant="body3" className="text-auth-ink">
            {displayStatus.text}
          </Typography>
        </Chip>
        {isInModeration && (
          <ResponsiveTooltip
            content={t('partner.partnerCampaigns.moderationTooltip')}
            placement="top"
            classNames={{
              content: 'max-w-[15.625rem] text-xs',
            }}
          >
            <span>
              <InfoCircle />
            </span>
          </ResponsiveTooltip>
        )}
      </div>

      {/* Start Date */}
      <div className="flex items-center gap-1">
        <IcDate size={14} />
        <span className="text-xs text-auth-ink leading-4">{formatDate(campaign.created_at)}</span>
      </div>

      {/* Last Update */}
      {timeAgo && (
        <div className="flex items-center flex-nowrap gap-1">
          <RefreshGreyIc size={14} />
          <span className="text-xs text-auth-muted whitespace-nowrap leading-4">{timeAgo}</span>
        </div>
      )}
    </div>
  );
};

export default StatusCell;
